import React, { useState, useEffect } from "react";
import { Search, MapPin, Ban, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useDebounce } from "@/hooks/useDebounce";
import { usePackerCorrection, type InventoryResult } from "@/hooks/usePackerCorrection";

interface PackerCorrectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (item: InventoryResult) => Promise<void>;
  productTitle?: string;
  productVariant?: string;
  ordernumber: string;
  lineitemindex: number;
}

const PackerCorrectionDialog = ({ open, onOpenChange, onSelect, productTitle, productVariant, ordernumber, lineitemindex }: PackerCorrectionDialogProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 200);
  const { searchInventory, saveNoMatch, searchResults, isSearching, isSaving, clearResults } = usePackerCorrection();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      clearResults();
    }
  }, [open, clearResults]);

  useEffect(() => {
    searchInventory(debouncedSearch);
  }, [debouncedSearch, searchInventory]);

  const handleSelect = async (item: InventoryResult) => {
    await onSelect(item);
    onOpenChange(false);
  };

  const handleNoMatch = async () => {
    const success = await saveNoMatch(ordernumber, lineitemindex);
    if (success) {
      // Optimistic cache update for "no base needed"
      queryClient.setQueriesData<any>(
        { queryKey: ['packers-list'] },
        (old: any) => {
          if (!old) return old;
          const updateOrders = (orders: any[]) =>
            orders.map((order: any) =>
              order.ordernumber === ordernumber
                ? {
                    ...order,
                    line_items: (order.line_items || []).map((li: any) =>
                      li.lineitemindex === lineitemindex
                        ? {
                            ...li,
                            base_paint: {
                              ...li.base_paint,
                              ignored_brand: true,
                              match_type: 'handmatig',
                            },
                          }
                        : li
                    ),
                  }
                : order
            );
          if (Array.isArray(old?.data)) {
            return { ...old, data: updateOrders(old.data) };
          }
          if (Array.isArray(old)) {
            return updateOrders(old);
          }
          return old;
        }
      );
      queryClient.invalidateQueries({ queryKey: ['packers-list'] });
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1a1f3d] border-white/10 text-white max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-white text-base">Correctie basisverf</DialogTitle>
          {productTitle && (
            <p className="text-white/50 text-xs truncate mt-1">voor: {productTitle}{productVariant ? ` — ${productVariant}` : ''}</p>
          )}
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            placeholder="Zoek in voorraad..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30"
            autoFocus
          />
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleNoMatch}
          disabled={isSaving}
          className="w-full bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:text-white"
        >
          {isSaving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Ban className="w-3.5 h-3.5 mr-1.5" />}
          Geen basis nodig
        </Button>

        <div className="flex-1 overflow-y-auto min-h-0 space-y-1 mt-2">
          {isSearching && (
            <div className="text-center py-8 text-white/40 text-sm">Zoeken...</div>
          )}
          {!isSearching && debouncedSearch.length >= 2 && searchResults.length === 0 && (
            <div className="text-center py-8 text-white/40 text-sm">Geen resultaten</div>
          )}
          {searchResults.map((item, idx) => {
            const isOutOfStock = item.voorraad <= 0;
            return (
              <button
                key={`${item.product_id}-${item.locatie}-${idx}`}
                onClick={() => handleSelect(item)}
                className={`w-full text-left rounded-lg p-3 transition-colors ${
                  isOutOfStock
                    ? 'bg-red-500/10 border border-red-500/20 hover:bg-red-500/20'
                    : 'bg-white/5 border border-white/10 hover:bg-white/15'
                }`}
              >
                <div className="text-white/90 text-sm font-medium truncate">{item.product_naam}</div>
                <div className="flex items-center gap-3 mt-1 text-xs">
                  {item.eenheid && <span className="text-white/50">{item.eenheid}</span>}
                  <span className="text-white/50 flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {item.locatie}
                  </span>
                  <span className={`font-bold ${isOutOfStock ? 'text-red-400' : 'text-emerald-400'}`}>
                    {item.voorraad}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PackerCorrectionDialog;
